import * as React from 'react'
import {
  BranchOrCommitQueryResult,
  branchSchema,
  commitSchema,
  isBranch,
  isCommit,
} from '@basehub/data/replicache/index'
import { useQuery } from '@tanstack/react-query'
import { useReplicacheQueryV2 } from '~/hooks-data/use-replicache-query'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { invariant } from '@epic-web/invariant'
import { useSelectedBranchContentRequest } from '../branches/use-selected-content-request'
import { useActiveSpaceId } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { useDefaultBranch } from './use-default-branch'
import { useRouter } from 'next/navigation'
import { useWaitForNextPull_repoReplicache } from '~/hooks-data/use-wait-for-next-pull-repo-replicache'
import { usePlaygroundCookie } from '../playground/use-playground-id'
import { RECENTLY_CREATED_BRANCH_STORAGE_KEY } from '@basehub/data/replicache/mutators/repo-space/branch/mutators'

export const useSelectedBranchOrCommitQuery = () => {
  const router = useRouter()
  const { branchOrCommit: branchOrCommitFromUrl, isLoading } =
    useDashboardSlugs()
  const playgroundId = usePlaygroundCookie()
  const { isLoading: isLoadingReplicache, data: repoReplicacheData } =
    useRepoReplicache()
  const spaceId = useActiveSpaceId() ?? repoReplicacheData?.spaceId
  const replicache = repoReplicacheData?.replicache
  const replicacheQueryEnabled =
    !isLoading && !isLoadingReplicache && Boolean(replicache)
  const branchOrCommit = playgroundId ?? branchOrCommitFromUrl

  const query = useReplicacheQueryV2<BranchOrCommitQuery>({
    key: getSelectedBranchOrCommitQueryKey({
      spaceId,
      playgroundId,
      branchOrCommit: branchOrCommitFromUrl,
    }),
    replicache,
    fetcher: async (tx) => {
      const _branchOrCommit = await repoSpaceMutators.getBranchOrCommit(tx, {
        branchOrCommit,
      })
      const visualBranch = await repoSpaceMutators.getBranchOrCommit(tx, {
        branchOrCommit: branchOrCommitFromUrl,
      })

      const isNotStaringInPlayground =
        _branchOrCommit?.result &&
        isBranch(_branchOrCommit.result) &&
        _branchOrCommit.result.sourceBranchId !== visualBranch?.result?.id
      if (isNotStaringInPlayground) {
        return { branchOrCommit: visualBranch, visualBranch: null }
      }

      return { branchOrCommit: _branchOrCommit, visualBranch }
    },
    enabled: replicacheQueryEnabled,
    keepPreviousData: true,
  })

  const pullDone = useWaitForNextPull_repoReplicache()

  React.useEffect(() => {
    if (
      !branchOrCommit ||
      !query.data?.branchOrCommit?.result ||
      !pullDone ||
      !repoReplicacheData?.spaceId
    ) {
      return
    }

    const isRecentlyCreated =
      window.sessionStorage.getItem?.(RECENTLY_CREATED_BRANCH_STORAGE_KEY) ===
      branchOrCommit

    if (!query.data.branchOrCommit.isFallback) {
      // cool, we found the branch and not a fallback. remove it from session storage
      if (isRecentlyCreated) {
        window.sessionStorage.removeItem(RECENTLY_CREATED_BRANCH_STORAGE_KEY)
      }
      return
    }

    if (isRecentlyCreated) {
      // don't eagerly redirect pal
      return
    }

    // leave ourselves a couple of seconds to prevent the fast redirect. this is a nasty thing. we should fix the root cause. but don't have time right now.
    const timeout = setTimeout(() => {
      if (!query.data?.branchOrCommit?.result) return
      const newUrl = new URL(window.location.href)
      newUrl.pathname = newUrl.pathname.replace(
        branchOrCommit,
        encodeURIComponent(
          isCommit(query.data.branchOrCommit.result)
            ? query.data.branchOrCommit.result.id
            : query.data.branchOrCommit.result.name
        )
      )
      router.push(newUrl.toString())
    }, 3000)

    return () => clearTimeout(timeout)
  }, [
    repoReplicacheData?.spaceId,
    branchOrCommit,
    query.data?.branchOrCommit?.isFallback,
    query.data?.branchOrCommit?.result,
    router,
    pullDone,
  ])

  const fallbackQueryEnabled =
    !isLoading &&
    !isLoadingReplicache &&
    !replicacheQueryEnabled &&
    !!spaceId &&
    !query.data

  const fallback = useQuery(
    getSelectedBranchOrCommitQueryKey({
      spaceId,
      branchOrCommit,
      playgroundId,
      fallback: true,
    }),
    async (): Promise<BranchOrCommitQuery> => {
      invariant(spaceId, 'spaceId must be defined')
      const searchParams = `?spaceId=${encodeURIComponent(spaceId)}`

      const [branchOrCommitResponse, visualBranchOrCommitResponse] =
        await Promise.all([
          manuallyFetchBranchOrCommit({ branchOrCommit, searchParams }),
          manuallyFetchBranchOrCommit({
            branchOrCommit: branchOrCommitFromUrl,
            searchParams,
          }),
        ])

      const isNotStaringInPlayground =
        branchOrCommitResponse?.result &&
        isBranch(branchOrCommitResponse.result) &&
        branchOrCommitResponse.result.sourceBranchId !==
          visualBranchOrCommitResponse?.result?.id

      if (isNotStaringInPlayground) {
        return {
          branchOrCommit: visualBranchOrCommitResponse,
          visualBranch: null,
        }
      }

      return {
        branchOrCommit: branchOrCommitResponse,
        visualBranch: visualBranchOrCommitResponse,
      }
    },
    {
      staleTime: Infinity,
      enabled: fallbackQueryEnabled,
    }
  )

  if (!replicacheQueryEnabled) {
    return {
      ...fallback,
      data: fallback.data?.branchOrCommit?.result ?? null,
      visualBranch: query.data?.visualBranch?.result ?? null,
    }
  }

  return {
    ...query,
    data: query.data?.branchOrCommit?.result ?? null,
    visualBranch: query.data?.visualBranch?.result ?? null,
  }
}

export const useSelectedBranch = () => {
  const { data, visualBranch } = useSelectedBranchOrCommitQuery()
  const cr = useSelectedBranchContentRequest()

  if (!data) return {}
  if (isCommit(data)) return {}
  if (!visualBranch || isCommit(visualBranch)) return { branch: data, cr }
  return { branch: data, visualBranch, cr }
}

export const getSelectedBranchOrCommitQueryKey = ({
  spaceId,
  branchOrCommit,
  playgroundId,
  fallback,
}: {
  spaceId: string | undefined
  branchOrCommit: string | null
  playgroundId: string | undefined
  fallback?: boolean
}) => {
  return [
    'branch-or-commit',
    spaceId,
    playgroundId ? `${playgroundId}-${branchOrCommit}` : branchOrCommit,
    fallback ? 'fallback' : 'replicache',
  ]
}

export const useBranchOrCommitSegment = (o?: {
  fallbackToDefaultBranch?: boolean
}) => {
  const { fallbackToDefaultBranch = true } = o ?? {}

  const rawBranchOrCommitSegment = useDashboardSlugs((s) => s.branchOrCommit)
  const { data: selectedBranchOrCommitData } = useSelectedBranchOrCommitQuery()
  const { data: defaultBranch } = useDefaultBranch()

  const segment =
    (rawBranchOrCommitSegment ||
      (selectedBranchOrCommitData
        ? isCommit(selectedBranchOrCommitData)
          ? selectedBranchOrCommitData.id
          : selectedBranchOrCommitData.name
        : undefined)) ??
    (fallbackToDefaultBranch ? defaultBranch?.name || 'main' : undefined)

  const encodedSegment = segment ? encodeURIComponent(segment) : undefined

  return {
    segment,
    encodedSegment,
    branchOrCommit: selectedBranchOrCommitData,
  }
}

type BranchOrCommitQuery = {
  branchOrCommit: BranchOrCommitQueryResult
  visualBranch: BranchOrCommitQueryResult
}

const manuallyFetchBranchOrCommit = async ({
  branchOrCommit,
  searchParams,
}: {
  branchOrCommit: string | null
  searchParams: string
}): Promise<BranchOrCommitQueryResult> => {
  return await fetch(
    branchOrCommit
      ? `/api/ad-hoc/branch-or-commit/${encodeURIComponent(
          branchOrCommit
        )}${searchParams}`
      : `/api/ad-hoc/branch-or-commit${searchParams}`
  )
    .then(async (res) => {
      if (!res.ok) {
        throw new Error('Failed to fetch branch or commit')
      }

      const json = await res.json()
      const parsedAsCommitJson = commitSchema.safeParse(json)

      if (parsedAsCommitJson.success) {
        return {
          result: parsedAsCommitJson.data,
          isFallback: true,
        }
      }

      return {
        result: branchSchema.parse(json),
        isFallback: true,
      }
    })
    .catch((error) => {
      console.error('Error fetching branch or commit:', error)
      return null
    })
}
